import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractApi } from '../../services/api';

/** A message or event in the contract's draft conversation (`GET /contracts/:id/messages`). */
export interface DraftMessage {
  id: string;
  userId: string;
  userName: string;
  userRole: string;
  message: string;
  draftAction?: string;
  createdAt: string;
}

/** Loads the contract's draft conversation; empty while loading, without a contract or on error. */
export function useDraftMessages(contractId: string | undefined) {
  const [messages, setMessages] = useState<DraftMessage[]>([]);
  useEffect(() => {
    let active = true;
    setMessages([]);
    if (!contractId) return undefined;
    contractApi.getDraftMessages(contractId)
      .then(loaded => { if (active) setMessages(Array.isArray(loaded) ? loaded : []); })
      .catch(() => { /* The history is context only; signing works without it. */ });
    return () => { active = false; };
  }, [contractId]);
  return messages;
}

const ACTION_KEYS: Record<string, string> = {
  sent: 'projectView.draftAction.newDraft',
  rejected: 'projectView.draftAction.rejected',
  reject: 'projectView.draftAction.rejected',
  approved: 'projectView.draftAction.approved',
  approve: 'projectView.draftAction.approved',
  signed_by_company: 'projectView.draftAction.signedByCompany',
  signed_by_person: 'projectView.draftAction.signedByPerson',
  // A plain comment gets no label: the name and role already say who wrote it,
  // whichever party it was.
};

const ROLE_KEYS: Record<string, string> = {
  person: 'persona.person',
  company: 'persona.company',
  admin: 'persona.admin',
};

/**
 * The draft conversation with the Beställare (returns, comments, signings), in
 * the review step so the Hantverkare sees what was said before signing again.
 * Oldest first, like the other threads; the company's own messages on the right.
 */
export function DraftMessageHistory({ messages }: { messages: DraftMessage[] }) {
  const { t } = useTranslation();
  const ids = useId();
  if (messages.length === 0) return null;
  const ordered = [...messages].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
  );
  return (
    <section className="contract-review-thread" aria-labelledby={`${ids}-history`} data-testid="draft-message-history">
      <h3 id={`${ids}-history`} className="contract-review-thread-title">{t('contracts.history.title')}</h3>
      <ul className="list-unstyled mb-0 d-flex flex-column gap-2 contract-wizard-history-list">
        {ordered.map(entry => {
          const actionKey = entry.draftAction ? ACTION_KEYS[entry.draftAction] : undefined;
          const roleKey = ROLE_KEYS[entry.userRole];
          return (
            <li key={entry.id} className={`contract-review-message${entry.userRole === 'company' ? ' is-mine' : ''}`}>
              <div className="contract-review-message-meta">
                <span>
                  <strong>{entry.userName}</strong>
                  {roleKey && <> · {t(roleKey)}</>}
                  {actionKey && <> · {t(actionKey)}</>}
                </span>
                <span>
                  {new Date(entry.createdAt).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {entry.message?.trim() && <p className="mb-0">{entry.message}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default DraftMessageHistory;
